<script setup lang="ts">
// "Find the Information You Need" — mockup page 1, lower band.
//
// Three content doors plus the local-help panel. The mockup puts a
// technician photo behind that last panel; the redesign hasn't sourced its
// photography yet, so it renders as a navy panel for now — same weight in
// the grid, no placeholder stock filler.
import { RouterLink } from 'vue-router'

const CARDS = [
    {
        to: '/cost-guides',
        title: 'AC Repair Cost Guide',
        body: 'See what common repairs cost in 2026. No surprises.',
        cta: 'View Cost Guide →',
        icon: '<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>',
    },
    {
        to: '/repair-or-replace',
        title: 'Repair or Replace Calculator',
        body: 'Get a clear recommendation based on your home and system.',
        cta: 'Try the Calculator →',
        icon: '<path d="M10 2.2v2.1M3.4 5.6h13.2M6.3 5.6 3.4 12h5.8zM13.7 5.6 10.8 12h5.8zM7.4 17.2h5.2v-1.4H7.4z"/><path d="M9.2 4.9h1.6v11.6H9.2z"/>',
    },
    {
        to: '/system-types',
        title: 'System Comparisons',
        body: 'Single-stage vs. variable-speed vs. communicating systems.',
        cta: 'Learn More →',
        icon: '<path d="M10 6.6A3.4 3.4 0 1 0 10 13.4 3.4 3.4 0 0 0 10 6.6zm0 5a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2z"/><path d="M17 11.2v-2.4l-1.9-.3a5.4 5.4 0 0 0-.6-1.4l1.1-1.6-1.7-1.7-1.6 1.1a5.4 5.4 0 0 0-1.4-.6L10.6 2.4H8.2l-.3 1.9c-.5.1-1 .3-1.4.6L4.9 3.8 3.2 5.5l1.1 1.6c-.3.4-.5.9-.6 1.4l-1.9.3v2.4l1.9.3c.1.5.3 1 .6 1.4l-1.1 1.6 1.7 1.7 1.6-1.1c.4.3.9.5 1.4.6l.3 1.9h2.4l.3-1.9c.5-.1 1-.3 1.4-.6l1.6 1.1 1.7-1.7-1.1-1.6c.3-.4.5-.9.6-1.4z"/>',
    },
]
</script>

<template>
    <section class="info">
        <div class="inner">
            <h2>Find the Information You Need</h2>

            <div class="grid">
                <RouterLink v-for="card in CARDS" :key="card.to" class="card" :to="card.to">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="card.icon"></svg>
                    </span>
                    <h3>{{ card.title }}</h3>
                    <p>{{ card.body }}</p>
                    <span class="link">{{ card.cta }}</span>
                </RouterLink>

                <div class="local">
                    <span class="pin" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 1.8a5.2 5.2 0 0 0-5.2 5.2c0 3.9 5.2 11.2 5.2 11.2s5.2-7.3 5.2-11.2A5.2 5.2 0 0 0 10 1.8zm0 7.2a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
                        </svg>
                    </span>
                    <h3>Local HVAC Help</h3>
                    <p>Connect with an HVAC professional in your area.</p>
                    <RouterLink class="cta" to="/second-opinion">Get Local Help →</RouterLink>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.info {
    background: var(--site-bg);
}

.inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.5rem, 2.6vw, 2rem);
    font-weight: 800;
    color: var(--site-navy);
    text-align: center;
    margin: 0 0 var(--site-space-xl);
}

.grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--site-space-md);

    @media (max-width: 940px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.4rem;
    padding: var(--site-space-lg) var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    box-shadow: var(--site-shadow-sm);
    text-decoration: none;
    transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;

    &:hover {
        transform: translateY(-2px);
        border-color: var(--site-blue);
        box-shadow: var(--site-shadow-md);
    }

    h3 {
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        font-weight: 700;
        color: var(--site-navy);
        margin: 0;
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        line-height: 1.45;
        color: var(--site-text-muted);
    }

    .link {
        margin-top: auto;
        padding-top: 0.5rem;
        font-size: 0.8rem;
        font-weight: 700;
        color: var(--site-blue);
    }
}

.chip {
    display: grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: var(--site-blue-soft);
    color: var(--site-blue);

    svg {
        width: 1.5rem;
        height: 1.5rem;
    }
}

.local {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.4rem;
    padding: var(--site-space-lg) var(--site-space-md);
    border-radius: var(--site-radius-md);
    background: var(--site-gradient-navy);
    color: #fff;

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1.05rem;
        font-weight: 700;
        margin: 0;
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        color: rgba(255, 255, 255, 0.82);
    }
}

.pin {
    display: grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    color: #fbbf24;

    svg {
        width: 1.5rem;
        height: 1.5rem;
    }
}

.cta {
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    @media (max-width: 860px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        font-size: 0.9rem;
    }
}
</style>
